import React, { useState } from 'react';
import { ContactButtons } from '../common';

// Title-case words: "new york" → "New York"
const capitalizeWords = (str) => {
  if (!str) return str;
  return str.replace(/\b\w/g, char => char.toUpperCase());
};

// Utility function to format phone numbers
const formatPhoneNumber = (phoneNumber) => {
  if (!phoneNumber) return '';

  // Remove all non-digit characters
  const digits = phoneNumber.replace(/\D/g, '');

  // Handle different digit lengths
  if (digits.length === 10) {
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
  } else if (digits.length === 11 && digits[0] === '1') {
    return `(${digits.slice(1, 4)}) ${digits.slice(4, 7)}-${digits.slice(7)}`;
  } else if (digits.length > 10) {
    // Treat last 10 digits as the phone number
    const last10 = digits.slice(-10);
    return `(${last10.slice(0, 3)}) ${last10.slice(3, 6)}-${last10.slice(6)}`;
  } else {
    return phoneNumber;
  }
};

function ProfileSidebar({ therapist, onContactClick }) {
  const [showShareModal, setShowShareModal] = useState(false);
  const [shareMessage, setShareMessage] = useState('');
  const [isTracking, setIsTracking] = useState(false);

  // Track contact interaction securely (HIPAA-compliant - no PHI stored)
  const trackContactInteraction = async (contactMethod, additionalData = {}) => {
    if (isTracking) return; // Prevent duplicate tracking
    setIsTracking(true);

    try {
      const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';
      await fetch(`${API_BASE_URL}/api/analytics/contact-interaction`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          therapistId: therapist._id,
          contactMethod, // 'message', 'phone', 'website'
          timestamp: new Date().toISOString(),
          referrer: document.referrer || '',
          userAgent: navigator.userAgent,
          ...additionalData
        }),
      });
    } catch (error) {
      console.error('Contact tracking error:', error);
      // Don't block user interaction if tracking fails
    } finally {
      setIsTracking(false);
    }
  };

  // Handle website visit with referral tracking
  const handleWebsiteVisit = (websiteUrl) => {
    if (!websiteUrl) return;

    // Ensure URL has protocol
    let formattedUrl = websiteUrl;
    if (!websiteUrl.startsWith('http://') && !websiteUrl.startsWith('https://')) {
      formattedUrl = `https://${websiteUrl}`;
    }

    try {
      // Add referral parameters for tracking
      const url = new URL(formattedUrl);
      url.searchParams.set('ref', 'therapylist');
      url.searchParams.set('therapist_id', therapist._id);
      url.searchParams.set('source', 'profile_sidebar');
      url.searchParams.set('utm_source', 'therapylist');
      url.searchParams.set('utm_medium', 'referral');
      url.searchParams.set('utm_campaign', 'therapist_profile');

      trackContactInteraction('website', {
        originalUrl: websiteUrl,
        trackedUrl: url.toString()
      });

      // Open in new tab
      window.open(url.toString(), '_blank', 'noopener,noreferrer');
    } catch (error) {
      console.error('Invalid website URL:', error);
      // Fallback: try opening the original URL
      window.open(formattedUrl, '_blank', 'noopener,noreferrer');
    }
  };

  const handleShare = async () => {
    const url = window.location.href;
    const title = `${therapist.firstName} ${therapist.lastName} - ${therapist.title || 'Licensed Therapist'}`;
    const text = `Check out ${therapist.firstName} ${therapist.lastName}, a ${therapist.title || 'Licensed Therapist'}${therapist.location?.city ? ` in ${capitalizeWords(therapist.location.city)}, ${therapist.location.state}` : ''}`;

    // Try Web Share API first (mobile/modern browsers)
    if (navigator.share) {
      try {
        await navigator.share({ title, text, url });
        return;
      } catch (err) {
        // Fall back to copy to clipboard
      }
    }

    // Fallback: copy to clipboard
    try {
      await navigator.clipboard.writeText(url);
      setShareMessage('Profile link copied to clipboard!');
      setTimeout(() => setShareMessage(''), 3000);
    } catch (err) {
      setShowShareModal(true);
    }
  };

  // Location Map Component - Only show if location exists
  const LocationMap = () => {
    if (!therapist.location?.city || !therapist.location?.state) return null;

    const isVirtualOnly = therapist.serviceAreas?.virtualOnly;

    // For virtual-only therapists, show a simplified location card without map/directions
    if (isVirtualOnly) {
      return (
        <div className="bg-white rounded-2xl p-6 shadow-lg border border-[#254e70]/10 mb-6">
          <h3 className="text-xl font-bold text-[#254e70] mb-4 flex items-center gap-2">
            <svg className="w-5 h-5 text-[#9dacff]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
            </svg>
            Virtual Practice
          </h3>
          <div className="text-sm text-[#254e70]/80">
            <p className="font-medium">{therapist.practiceName || `${therapist.firstName}'s Practice`}</p>
            <p>{capitalizeWords(therapist.location.city)}, {therapist.location.state}</p>
          </div>
          <div className="mt-3 inline-flex items-center gap-2 px-3 py-1.5 bg-blue-50 text-blue-700 rounded-lg text-sm font-medium">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
            </svg>
            Online sessions only
          </div>
        </div>
      );
    }

    const address = `${therapist.location.address || ''} ${therapist.location.city}, ${therapist.location.state}`.trim();
    const encodedAddress = encodeURIComponent(address);

    // Only show map if Google Maps API key exists
    const hasGoogleMapsKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

    // Simplified Static Map
    const staticMapSrc = hasGoogleMapsKey ?
      `https://maps.googleapis.com/maps/api/staticmap?center=${encodedAddress}&zoom=16&size=400x300&maptype=roadmap&markers=color:blue%7C${encodedAddress}&key=${process.env.REACT_APP_GOOGLE_MAPS_API_KEY}` : null;

    // Fallback embed map
    const mapSrc = hasGoogleMapsKey ?
      `https://www.google.com/maps/embed/v1/place?key=${process.env.REACT_APP_GOOGLE_MAPS_API_KEY}&q=${encodedAddress}&zoom=16&maptype=roadmap` : null;

    return (
      <div className="bg-white rounded-2xl p-6 shadow-lg border border-[#254e70]/10 mb-6">
        <h3 className="text-xl font-bold text-[#254e70] mb-4 flex items-center gap-2">
          <svg className="w-5 h-5 text-[#9dacff]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
          </svg>
          Practice Location
        </h3>

        <div className="space-y-3">
          <div className="text-sm text-[#254e70]/80">
            <p className="font-medium">{therapist.practiceName || `${therapist.firstName}'s Practice`}</p>
            {therapist.location.address && <p>{therapist.location.address}</p>}
            <p>{capitalizeWords(therapist.location.city)}, {therapist.location.state} {therapist.location.zipCode}</p>
          </div>

          {/* Map */}
          <div className="relative rounded-xl overflow-hidden border border-[#254e70]/10">
            {hasGoogleMapsKey ? (
              <>
                <img
                  src={staticMapSrc}
                  alt={`Map showing ${address}`}
                  className="w-full h-48 object-cover rounded-xl"
                  onError={(e) => {
                    // Fallback to iframe if static map fails
                    e.target.style.display = 'none';
                    e.target.nextElementSibling.style.display = 'block';
                  }}
                />

                {/* Fallback iframe (hidden by default) */}
                <iframe
                  src={mapSrc}
                  title="Therapist location map"
                  width="100%"
                  height="192"
                  style={{ border: 0, display: 'none' }}
                  allowFullScreen=""
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  className="rounded-xl"
                />
              </>
            ) : (
              <div className="w-full h-48 bg-gray-50 flex items-center justify-center rounded-xl border-2 border-dashed border-gray-200">
                <div className="text-center text-gray-400">
                  <svg className="w-12 h-12 mx-auto mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                  <p className="text-sm font-medium">Location</p>
                  <p className="text-xs">{capitalizeWords(therapist.location.city)}, {therapist.location.state}</p>
                </div>
              </div>
            )}
          </div>

          {/* Get Directions Link */}
          <a
            href={`https://www.google.com/maps/search/?api=1&query=${encodedAddress}`}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 text-[#9dacff] hover:text-[#254e70] transition-colors text-sm font-medium"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" />
            </svg>
            Get Directions
          </a>
        </div>
      </div>
    );
  };

  return (
    <>
      {/* Location Map */}
      <LocationMap />

      <div className="bg-white rounded-2xl p-6 shadow-lg border border-[#254e70]/10 sticky top-6">
        <h3 className="text-xl font-bold text-[#254e70] mb-6">At a Glance</h3>
        
        <div className="space-y-5">
          {/* Who They Work With */}
          {therapist.ageGroups?.length > 0 && (
            <div>
              <p className="text-sm font-medium text-[#254e70] mb-2">Works With</p>
              <div className="flex flex-wrap gap-2">
                {therapist.ageGroups.map((age, index) => (
                  <span key={index} className="text-xs px-3 py-1 bg-[#9dacff]/10 text-[#254e70] rounded-full border border-[#9dacff]/20">
                    {typeof age === 'object' ? age.name : age}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Top Specialties */}
          {therapist.specializations?.length > 0 && (
            <div>
              <p className="text-sm font-medium text-[#254e70] mb-2">Specializes In</p>
              <div className="space-y-1">
                {therapist.specializations.slice(0, 4).map((specialty, index) => (
                  <div key={index} className="text-sm text-[#254e70]/80 flex items-center gap-2">
                    <span className="w-1.5 h-1.5 bg-[#9dacff] rounded-full"></span>
                    {typeof specialty === 'object' ? specialty.name : specialty}
                  </div>
                ))}
                {therapist.specializations.length > 4 && (
                  <p className="text-xs text-[#254e70]/60 mt-1 ml-3">
                    +{therapist.specializations.length - 4} more specialties
                  </p>
                )}
              </div>
            </div>
          )}

          {/* Languages */}
          {therapist.professional?.languages?.length > 0 && (
            <div>
              <p className="text-sm font-medium text-[#254e70] mb-2">Languages</p>
              <p className="text-sm text-[#254e70]/80">
                {therapist.professional.languages.slice(0, 3).join(', ')}
                {therapist.professional.languages.length > 3 && ` +${therapist.professional.languages.length - 3} more`}
              </p>
            </div>
          )}

          {/* Service Options */}
          {(therapist.sessionTypes?.length > 0 || therapist.rates?.freeConsultation?.offered || therapist.serviceAreas?.virtualOnly) && (
            <div>
              <p className="text-sm font-medium text-[#254e70] mb-2">Available Options</p>
              <div className="flex flex-wrap gap-2">
                {therapist.serviceAreas?.virtualOnly && (
                  <span className="text-xs px-3 py-1 bg-blue-100 text-blue-700 rounded-full">💻 Online</span>
                )}
                {therapist.sessionTypes?.includes('in-person') && (
                  <span className="text-xs px-3 py-1 bg-green-100 text-green-700 rounded-full">🏢 In-Person</span>
                )}
                {therapist.rates?.freeConsultation?.offered && (
                  <span className="text-xs px-3 py-1 bg-purple-100 text-purple-700 rounded-full">💬 Free Consult</span>
                )}
              </div>
            </div>
          )}

          {/* Contact Info */}
          <div className="pt-2 border-t border-[#254e70]/10">
            <p className="text-sm font-medium text-[#254e70] mb-3">Contact Information</p>
            
            <div className="space-y-3">
              {/* Rate */}
              {therapist.rates?.individual && (
                <div className="flex justify-between items-center">
                  <span className="text-sm text-[#254e70]/70">Session Rate</span>
                  <span className="text-sm font-medium text-[#254e70]">${therapist.rates.individual}</span>
                </div>
              )}

              {/* Phone */}
              {therapist.phone && (
                <div className="flex justify-between items-center">
                  <span className="text-sm text-[#254e70]/70">Phone</span>
                  <a href={`tel:${therapist.phone}`} className="text-sm font-medium text-[#9dacff] hover:text-[#254e70] transition-colors">
                    {formatPhoneNumber(therapist.phone)}
                  </a>
                </div>
              )}

              {/* Website */}
              {therapist.website && (
                <div className="flex justify-between items-center">
                  <span className="text-sm text-[#254e70]/70">Website</span>
                  <button
                    onClick={() => handleWebsiteVisit(therapist.website)}
                    className="text-sm font-medium text-[#9dacff] hover:text-[#254e70] flex items-center gap-1 transition-colors"
                  >
                    Visit
                    <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                    </svg>
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Contact Buttons */}
          <div className="pt-4">
            <ContactButtons 
              therapist={therapist} 
              onContactClick={onContactClick}
              size="compact"
            />
          </div>

          {/* Share Profile */}
          <div className="pt-4 border-t border-[#254e70]/10">
            <button
              onClick={handleShare}
              className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-white hover:bg-gray-50 text-[#254e70] rounded-lg border-2 border-[#254e70] transition-colors text-sm font-medium"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.367 2.684 3 3 0 00-5.367-2.684z" />
              </svg>
              Share Profile
            </button>
            
            {shareMessage && (
              <p className="text-xs text-green-600 text-center mt-2">{shareMessage}</p>
            )}
          </div>
        </div>
      </div>

      {/* Share Modal */}
      {showShareModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 m-4 max-w-sm w-full">
            <h3 className="text-lg font-semibold text-[#254e70] mb-4">Share Profile</h3>
            <p className="text-sm text-[#254e70]/70 mb-4">Copy this link to share the profile:</p>
            <div className="bg-gray-100 p-3 rounded-lg mb-4">
              <input 
                type="text" 
                value={window.location.href} 
                readOnly 
                className="w-full bg-transparent text-sm text-[#254e70] outline-none"
                onClick={(e) => e.target.select()}
              />
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => setShowShareModal(false)}
                className="flex-1 px-4 py-2 bg-gray-200 text-[#254e70] rounded-lg font-medium hover:bg-gray-300 transition-colors"
              >
                Close
              </button>
              <button
                onClick={() => {
                  navigator.clipboard.writeText(window.location.href);
                  setShareMessage('Link copied!');
                  setShowShareModal(false);
                  setTimeout(() => setShareMessage(''), 3000);
                }}
                className="flex-1 px-4 py-2 bg-[#254e70] text-white rounded-lg font-medium hover:bg-[#1d3e5a] transition-colors"
              >
                Copy Link
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default ProfileSidebar; 